import {
  ReactElement, useCallback, useEffect, useState,
} from 'react';
import { EnergyConsumptionGoalContainer } from './styles';
import { EnergyConsumptionTable } from './EnergyConsumptionTable';
import { EnergyConsumptionRegister } from './EnergyConsumptionRegister';
import { DeleteModal } from './components/DeleteModal';
import { GetGoalsResponse, GoalTableItem, SetGoalsResponse } from './types';
import { useToast } from 'contexts/newToastContext';
import { useTranslation } from 'react-i18next';
import { prepareDeleteGoalsParams, setEnergyGoals } from './helpers';
import { FormModal } from './components/FormModal';
import { getUserProfile } from 'helpers/userProfile';
import { handleUnitStatusByProfile } from '~/helpers/unitStatus';
import { getEnergyGoals } from 'helpers/energyGoals';

export function EnergyConsumptionGoal({
  clientId,
}: Readonly<{
  clientId: number
}>): ReactElement {
  const [profile] = useState(getUserProfile);
  const [allData, setAllData] = useState<GetGoalsResponse>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [mode, setMode] = useState<'register' | 'edit'>('register');
  const [info, setInfo] = useState<GoalTableItem>();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onClickDelete = useCallback((row: GoalTableItem): void => {
    setOpenDeleteModal(true);
    setInfo(row);
  }, []);

  const onClickEdit = useCallback((row: GoalTableItem): void => {
    setInfo(row);
    setMode('edit');
    setOpenFormModal(true);
  }, []);

  const onClickRegisterPlatform = useCallback((): void => {
    setMode('register');
    setOpenFormModal(true);
  }, []);

  function onCloseFormModal(): void {
    setInfo(undefined);
    setOpenFormModal(false);
  }

  async function onSubmitDelete(): Promise<void> {
    if (!info) return;

    setLoading(true);

    const params = prepareDeleteGoalsParams(info.UNIT_ID, info.YEAR, info.GOALS);
    const response = await setEnergyGoals(params);

    setLoading(false);

    if (response) {
      toast(t('sucessoTitulo'), t('sucessoSalvarInformacoes'), 'success');
      setInfo(undefined);
      setAllData((data) => data.filter((item) => !(item.UNIT_ID === params.UNIT_ID && item.YEAR === params.YEAR)));
      onCloseDeleteModal();
    } else {
      toast(t('erroTitulo'), t('erroSalvarInformacoes'), 'error');
    }
  }

  function onCloseDeleteModal(): void {
    setOpenDeleteModal(false);
  }

  function onSuccessForm(responses: SetGoalsResponse[]): void {
    setAllData((currentData) => {
      const map = new Map(currentData.map((item) => [`${item.UNIT_ID}-${item.YEAR}`, item]));

      for (const response of responses) {
        if (response.GOALS.length === 0) {
          map.delete(`${response.UNIT_ID}-${response.YEAR}`);
          continue;
        }

        map.set(`${response.UNIT_ID}-${response.YEAR}`, response);
      }

      return Array.from(map.values());
    });
  }

  async function getEnergyGoalsList() {
    setLoadingData(true);

    const response = await getEnergyGoals({
      CLIENT_ID: Number(clientId),
      INCLUDE_UNIT_STATUS: handleUnitStatusByProfile(profile),
    });

    setAllData(response);

    setLoadingData(false);
  }

  useEffect(() => {
    if (clientId) {
      getEnergyGoalsList();
    }
  }, []);

  return (
    <>
      {info && (
        <DeleteModal
          isOpen={openDeleteModal}
          loading={loading}
          unitName={info.UNIT_NAME}
          year={info.YEAR}
          onClose={onCloseDeleteModal}
          onClickDelete={onSubmitDelete}
        />
      )}
      <FormModal
        clientId={clientId}
        isOpen={openFormModal}
        mode={mode}
        data={allData}
        selectedInfo={info ? {
          unitId: info.UNIT_ID,
          year: info.YEAR,
        } : undefined}
        onClose={onCloseFormModal}
        onSuccess={onSuccessForm}
      />
      <EnergyConsumptionGoalContainer>
        <EnergyConsumptionRegister
          clientId={clientId}
          onClickRegisterPlatform={onClickRegisterPlatform}
        />
        <EnergyConsumptionTable
          data={allData}
          loading={loadingData}
          onDelete={onClickDelete}
          onEdit={onClickEdit}
        />
      </EnergyConsumptionGoalContainer>
    </>
  );
}
